import React from 'react';
import { ArrowRight, LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon?: LucideIcon;
  iconColor?: string;
  linkTo?: string;
  trend?: {
    value: number;
    label: string;
    isPositive?: boolean;
  };
  breakdown?: Array<{
    label: string;
    value: number | string;
    color?: string;
  }>;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  iconColor = 'bg-naviter-blue',
  linkTo,
  trend,
  breakdown
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (linkTo) {
      navigate(linkTo);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`bg-white rounded-lg shadow p-6 ${
        linkTo ? 'cursor-pointer hover:shadow-md transition-shadow border-2 border-transparent hover:border-naviter-blue' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            {Icon && (
              <div className={`w-10 h-10 ${iconColor} rounded-lg flex items-center justify-center`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <h3 className="text-sm font-medium text-gray-500">{title}</h3>
              <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
            </div>
          </div>
          
          {subtitle && (
            <p className="text-sm text-gray-500 mt-2">{subtitle}</p>
          )}
          
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${
              trend.isPositive ? 'text-green-600' : 'text-gray-500'
            }`}>
              <span className="font-medium">{trend.value > 0 ? '+' : ''}{trend.value}</span>
              <span>{trend.label}</span>
            </div>
          )}
          
          {breakdown && breakdown.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="space-y-2">
                {breakdown.map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{item.label}</span>
                    <span className={`font-medium ${item.color || 'text-gray-900'}`}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {linkTo && (
          <ArrowRight className="w-5 h-5 text-gray-400" />
        )}
      </div>
    </div>
  );
};

export default StatCard;

